import React from 'react';

import { useTheme } from '../../hooks';
import { CalendarMonthIcon } from '../Icons';
import { Grid } from '../Layout';
import { TimeSelector } from '../TimeSelector';
import Typography from '../Typography';

import { DatePickerProps, LocaleDateVariant } from '../../types';
import { Select } from '../Select';
import { MenuItem } from '../MenuItem';
import { LocaleDate } from '../LocaleDate';
import useMediaQuery from '@mui/material/useMediaQuery';

export const DatePicker = ({
    'data-testid': dataTestId,
    onClick,
    onChange,
    onCalendarIconClick,
    options,
    promptText,
    value,
    sx,
    platform,
    variant = LocaleDateVariant.shortMonthYear,
    collapseDatePicker
}: DatePickerProps) => {
    const theme = useTheme();
    const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const data = options.map((option, index) => ({
        id: `date-option-${index}`,
        boxHeight: '35px',
        color: theme.palette.lightGray.main,
        dateKey: option.value,
        disabled: option.disabled,
        label: option.label,
        onClick: () => onClick(option.value),
        value: option.value
    }));

    return (
        <Grid
            container
            sx={[
                (theme) => ({
                    ...theme.mixins.mapButton,
                    '&:hover, &:focus, &:focus-within': {},
                    padding: theme.spacing(2),
                    flexWrap: 'nowrap',
                    width: 'unset'
                }),
                ...(Array.isArray(sx) ? sx : [sx])
            ]}
            columnSpacing={1}
            flexDirection="row"
            alignItems="center"
            margin={0}
            className="msat_date-picker"
        >
            {/* icon */}
            <Grid size="auto">
                <CalendarMonthIcon
                    data-testid="calendar-month-icon"
                    sx={{
                        fontSize: '48px',
                        cursor: { xs: 'pointer', sm: 'default' }
                    }}
                    onClick={(e) => {
                        // generate a click event on the calendar icon for small screens when any mini legend chip is expanded
                        if (isMobileScreen && collapseDatePicker) {
                            onCalendarIconClick && onCalendarIconClick();
                            e.stopPropagation();
                        }
                    }}
                />
            </Grid>
            {/* prompt */}
            <Grid
                size={{ xs: 0, sm: 2, md: 2, lg: 2 }}
                sx={{
                    minWidth: { xs: 0, sm: 90 }
                }}
            >
                <Typography
                    sx={{
                        lineHeight: '1.25rem',
                        display: { xs: 'none', sm: 'block' }
                    }}
                >
                    {promptText}
                </Typography>
            </Grid>
            {/* date selector */}
            <Grid
                size="grow"
                // This is another magic number
                sx={{
                    display: { xs: 'none', sm: 'block' },
                    marginRight: -2
                }}
            >
                <TimeSelector
                    data-testid={dataTestId || 'date-picker'}
                    data={data}
                    platform={platform}
                    variant={variant}
                    value={value}
                    onChange={onChange}
                    layout="horizontal"
                    selectedValue={value}
                />
            </Grid>
            {/* use dropdown date selector on mobile */}
            <Grid size="grow" sx={{ display: { xs: collapseDatePicker ? 'none' : 'block', sm: 'none' } }}>
                <Select
                    data-testid={'global-date-picker-dropdown'}
                    onChange={onChange}
                    variant="standard"
                    value={value}
                    sx={{
                        '& .MuiSelect-nativeInput': {
                            pointerEvents: 'none'
                        }
                    }}
                >
                    {data.map((item) => {
                        return (
                            <MenuItem key={item.value} value={item.value}>
                                <LocaleDate date={item.value} variant={variant} />
                            </MenuItem>
                        );
                    })}
                </Select>
            </Grid>
        </Grid>
    );
};
